import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BillingCallback() {
  const [params] = useSearchParams();
  const { t } = useI18n();
  const { refreshMe } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("processing"); // processing | success | failed
  const attemptsRef = useRef(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      let paid = false;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") paid = true;
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("failed");
          return;
        }
      } catch (e) { /* Stripe upstream flakiness — fall through */ }

      // Fallback: check subscription state directly via webhook-driven update
      if (!paid) {
        try {
          const { data: me } = await api.get("/auth/me");
          const sub = me.subscription;
          if (sub && sub.effective_status === "active" && sub.current_period_end) {
            paid = true;
          }
        } catch (e) { /* ignore */ }
      }

      if (paid) {
        await refreshMe();
        setState("success");
        setTimeout(() => nav("/app/billing"), 1600);
        return;
      }

      if (attemptsRef.current >= 8) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen shell-gradient flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[var(--border-default)] p-10 text-center">
        {state === "processing" && (
          <>
            <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
            <h1 className="font-heading font-extrabold text-2xl text-ink">{t("billing.paymentProcessing")}</h1>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#2d8a56] mx-auto mb-4" />
            <h1 className="font-heading font-extrabold text-2xl text-ink">{t("billing.paymentSuccess")}</h1>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-[#b12e31] mx-auto mb-4" />
            <h1 className="font-heading font-extrabold text-2xl text-ink">{t("billing.paymentFailed")}</h1>
            <Link to="/app/billing" className="mt-4 inline-block px-4 py-2 rounded-xl bg-brand hover:bg-brand-hover text-white font-semibold">
              {t("common.back")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
